// app/(app)/payments/[id].tsx
import { Button } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  deletePayment,
  fetchPaymentById,
  resetSelectedPayment,
} from "@/store/slices/paymentSlice";
import { showAlert } from "@/utils/alert"; // ✅ cross‑platform alert
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

const isWithinOneHour = (createdAt: string): boolean => {
  const created = new Date(createdAt).getTime();
  const now = new Date().getTime();
  return now - created <= 60 * 60 * 1000; // 1 hour
};

export default function PaymentDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { selectedPayment, loading, updating, error } = useAppSelector(
    (state) => state.payments,
  );
  const { user } = useAppSelector((state) => state.auth);
  const isOwner = user?.userType === "Owner";
  const isEmployee = user?.userType === "Employee";

  useFocusEffect(
    useCallback(() => {
      if (id) {
        dispatch(fetchPaymentById(parseInt(id)));
      }
      return () => {
        dispatch(resetSelectedPayment());
      };
    }, [id]),
  );

  useEffect(() => {
    if (error) showAlert("Error", error);
  }, [error]);

  const handleDelete = () => {
    showAlert(
      "Delete Payment",
      "Are you sure you want to delete this payment? This action will revert the customer balance.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            const result = await dispatch(deletePayment(parseInt(id)));
            if (deletePayment.fulfilled.match(result)) {
              showAlert("Deleted", "Payment deleted successfully", [
                { text: "OK", onPress: () => router.back() },
              ]);
            }
          },
        },
      ],
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (!selectedPayment) return null;

  const canDelete =
    (isOwner || isEmployee) &&
    !selectedPayment.deletedAt &&
    isWithinOneHour(selectedPayment.createdAt);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
    >
      <View style={styles.card}>
        <View style={styles.row}>
          <Text style={styles.label}>Customer</Text>
          <Text style={styles.value}>
            {selectedPayment.Customer?.fullName || "Unknown"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Phone</Text>
          <Text style={styles.value}>
            {selectedPayment.Customer?.phone || "N/A"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Amount</Text>
          <Text style={[styles.value, styles.amount]}>
            ₹{parseFloat(selectedPayment.amount).toFixed(2)}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Type</Text>
          <Text style={styles.value}>
            {selectedPayment.paymentType.toUpperCase()}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Date</Text>
          <Text style={styles.value}>
            {new Date(selectedPayment.paymentDate).toLocaleDateString()}
          </Text>
        </View>
        {selectedPayment.referenceNo && (
          <View style={styles.row}>
            <Text style={styles.label}>Reference</Text>
            <Text style={styles.value}>{selectedPayment.referenceNo}</Text>
          </View>
        )}
        <View style={styles.row}>
          <Text style={styles.label}>Recorded By</Text>
          <Text style={styles.value}>
            {selectedPayment.creator?.ownerName || "System"}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Created At</Text>
          <Text style={styles.value}>
            {new Date(selectedPayment.createdAt).toLocaleString()}
          </Text>
        </View>
        {selectedPayment.deletedAt && (
          <View style={styles.row}>
            <Text style={[styles.label, { color: colors.danger }]}>
              Deleted At
            </Text>
            <Text style={[styles.value, { color: colors.danger }]}>
              {new Date(selectedPayment.deletedAt).toLocaleString()}
            </Text>
          </View>
        )}
      </View>

      {canDelete && (
        <Button
          title="Delete Payment"
          onPress={handleDelete}
          loading={updating}
          variant="danger"
          containerStyle={styles.deleteButton}
        />
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#fff" },
  scrollContent: { padding: 16, paddingBottom: 40 },
  loadingContainer: { flex: 1, justifyContent: "center", alignItems: "center" },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    marginBottom: 20,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#f3f4f6",
  },
  label: { fontSize: 14, color: "#6b7280" },
  value: { fontSize: 14, color: "#111827", fontWeight: "500" },
  amount: { fontSize: 18, fontWeight: "700", color: "#4f46e5" },
  deleteButton: { marginTop: 16 },
});
